// src/features/intervene/services/interveneService.js
import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';
import {
  INTERVENE_LIST_PAGE_SIZE,
  INTERVENE_MESSAGE_PAGE_SIZE,
} from '../constants/interveneApi';

/**
 * Pure Data Access Layer.
 * These functions ONLY handle network requests. They do not touch React State.
 */
export const interveneService = {

  async getAllChannels(companyId) {
    if (!companyId) throw new Error('Company ID is required to fetch channels');
    return await APIService.get(`${Apis.whatsappBusinessAccounts}?company_id=${companyId}`);
  },

  async getUserList(filters) {
    if (!filters?.botNumber) throw new Error('Bot number is required');

    const params = new URLSearchParams({
      bot_number: filters.botNumber,
      user_status: filters.userType || '',
      next_page: String(filters.page || 1),
      page_limit: String(filters.pageLimit || INTERVENE_LIST_PAGE_SIZE),
      search: filters.search || '',
    });

    const companyId =
      filters.company_id ||
      filters.organisationId ||
      filters.organisation_id ||
      '';

    if (companyId) {
      params.set('company_id', String(companyId));
    }

    if (filters.campaignId) {
      params.set('campaign_id', filters.campaignId);
    }

    return await APIService.get(`${Apis.getActiveUser}?${params.toString()}`);
  },

  async getChatCounts(botNumber, organisationId) {
    if (!botNumber) throw new Error('Bot number is required');
    return await APIService.get(`${Apis.getChatUsersCounts}?bot_number=${botNumber}&company_id=${organisationId || ''}`);
  },

  async getUserMessages(filters) {
    if (!filters?.botNumber || !filters?.phone_number) {
      throw new Error('Bot number and phone number are required');
    }

    const params = new URLSearchParams({
      phone_number: filters.phone_number,
      bot_number: filters.botNumber,
      next_page: String(filters.page || 1),
      page_limit: String(filters.pageLimit || INTERVENE_MESSAGE_PAGE_SIZE),
    });

    const organisationId =
      filters.organisationId || filters.organisation_id || filters.company_id || '';
    if (organisationId) params.set('company_id', String(organisationId));

    if (filters.chat_type) params.set('chat_type', filters.chat_type);
    if (filters.intervened_by) params.set('intervened_by', filters.intervened_by);

    return await APIService.get(`${Apis.getMasterConversion}?${params.toString()}`);
  },

  async sendMessage(formData, botNumber) {
    if (!botNumber) throw new Error('Bot number is required');
    formData.append('bot_number', botNumber);
    
    return await APIService.post(`v0/ui/intervene/send-message/`, formData, {
      'Content-Type': 'multipart/form-data',
    });
  },

  async toggleSessionStatus(payload) {
    return await APIService.post(`${Apis.startEndSession}`, payload);
  },

  async getUserDetail(phoneNumber) {
    if (!phoneNumber) throw new Error('Phone number is required');
    return await APIService.get(`${Apis.getUserDetails}?phone_number=${phoneNumber}`);
  },

  async getUserJourney(phoneNumber, botNumber) {
    if (!phoneNumber) throw new Error('Phone number is required');
    let url = `${Apis.getUserJuorney}?phone_number=${phoneNumber}`;
    if (botNumber) url += `&bot_number=${botNumber}`;
    return await APIService.get(url);
  },

  async getDialerCallerIds() {
    return await APIService.get(Apis.getDialerCallerIds);
  },

  async getDialerAgents() {
    return await APIService.get(Apis.getDialerAgents);
  },

  async postDialerCall(payload) {
    return await APIService.post(Apis.postDialerCall, payload);
  },

  async getWhatsappTemplateList(filters = {}) {
    const params = new URLSearchParams({
      next_page: String((filters.page ?? 0) + 1),
      search: filters.search || '',
      sort_field: filters.sortField || 'created_at',
      sort_order: filters.sortOrder || 'desc',
      category: filters.category || '',
      bot_number: filters.botNumber || '',
      limit: String(filters.limit || 10),
      company_id: filters.companyId || '',
      status: filters.status || '',
    });

    return await APIService.get(`${Apis.getWhatsappTemplate}?${params}`);
  },

  async sendWhatsappTemplate(payload, botNumber) {
    if (!botNumber) throw new Error('Bot number is required');
    return await APIService.post(`${Apis.sendWhatsappTemplate}${botNumber}`, payload);
  },

  async uploadTemplateHeaderFile(formData, botNumber) {
    if (!botNumber) throw new Error('Bot number is required');
    return await APIService.post(`${Apis.uploadHeaderFile}?bot_number=${botNumber}`, formData, {
      'Content-Type': 'multipart/form-data',
    });
  },

  async getOrgAgents() {
    return await APIService.get(Apis.getInterveneOrgAgents);
  },

  async getAgentStatuses(organisationId) {
    const params = organisationId ? `?organisation_id=${organisationId}` : '';
    return await APIService.get(`${Apis.getAgentStatuses}${params}`);
  },

  async updateAgentStatus(status, organisationId) {
    const payload = { status };
    if (organisationId) payload.organisation_id = organisationId;
    return await APIService.patch(Apis.getAgentStatuses, payload);
  },
};
